import { useEffect, useMemo, useState } from 'react';
import { Chip, InputAdornment, Paper, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const Corrections = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });

  const columns = useMemo(
    () => [
      {
        field: 'employee',
        headerName: 'Employee',
        minWidth: 190,
        renderCell: (params) => (
          <Typography>
            {params.row.userId?.firstName} {params.row.userId?.lastName}
          </Typography>
        ),
      },
      {
        field: 'date',
        headerName: 'Date',
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {moment(params.row.date).format('DD/MM/YYYY')}
          </Typography>
        ),
      },
      {
        field: 'attendanceId',
        headerName: 'Attendance Time',
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {`${moment(params.row.attendanceId.loginTime).format('HH : mm A')}- ${moment(params.row.attendanceId.logoutTime).format('HH : mm A')}`}
          </Typography>
        ),
      },
      {
        field: 'requestedLogin',
        headerName: 'Requested Time',
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {`${moment(params.row.requestedLogin).format('HH : mm A')}- ${moment(params.row.requestedLogout).format('HH : mm A')}`}
          </Typography>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const getColor = (status) => {
            switch (status) {
              case 'APPROVED':
                return 'success';
              case 'REJECTED':
                return 'error';
              case 'PENDING':
                return 'warning';
              default:
                return 'default';
            }
          };

          return (
            <Chip
              label={params.row.status}
              color={getColor(params.row.status)}
              size="small"
              sx={{ fontWeight: 500 }}
            />
          );
        },
      },
      {
        field: 'appliedOn',
        headerName: 'Update On',
        minWidth: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {moment(params.row.updatedAt).format('DD/MM/YYYY HH:mm')}
          </Typography>
        ),
      },
      {
        field: 'managerId',
        headerName: 'Approved By',
        minWidth: 180,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => {
          const approvedBy = params.row?.managerId;
          return (
            <Typography variant="subtitle2">
              {approvedBy ? `${approvedBy.firstName} ${approvedBy.lastName}` : '-'}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await post('users/getCorrections', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
        admin: true,
      });
      setRows(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Error fetching leaves', {
        variant: 'error',
        autoHideDuration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({
        ...prev,
        search,
        page: 0,
      }));
    }, 600);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchLeaves();
  }, [paginationModel]);

  return (
    <Paper sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
      <StyledTextField
        type="search"
        fullWidth
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search users"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          maxWidth: { sm: 200, md: 240 },
          mb: 3,
        }}
      />
      <DataGrid
        loading={loading}
        rows={rows}
        columns={columns}
        getRowId={(row) => row._id}
        pagination
        paginationMode="server"
        rowCount={total}
        paginationModel={paginationModel} // { page: 0, pageSize: 10 }
        onPaginationModelChange={(e) => {
          setPaginationModel(() => ({
            page: e.page,
            pageSize: e.pageSize,
            search: '',
          }));
        }}
        pageSizeOptions={[5, 10, 20]}
        slots={{
          basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
        }}

        // getRowId={(row) => row._id}
        // rowCount={total}
        // paginationModel={paginationModel}
        // onPaginationModelChange={(model) =>
        //   setPaginationModel({
        //     page: model.page,
        //     pageSize: model.pageSize,
        //     search,
        //   })
        // }
        // pageSizeOptions={[5, 10, 20]}
      />
    </Paper>
  );
};

export default Corrections;
